import { Autocomplete, Box, FormHelperText, TextField } from '@mui/material';
import { useController, FieldValues, UseFormSetValue } from 'react-hook-form';
import { HookFormComponentProps } from 'ui-component/HookFormComponents/types';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  useGetProductionRunsQuery,
  useLazyGetProductionRunQuery,
} from 'store/slices/apiV1/production';
import ContextualCreateButton from 'ui-component/ContextualCreateButton';
import { ALL_APP_IDS, ALL_APPS } from 'constants/appConstants';
import CreateEditProductionRunDialog from 'views/production/components/CreateEditProductionRunDialog';
import _ from 'lodash';
import { ProductionRun, ProductionRunLite } from 'types/production';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { TeamLite } from 'types/properties';
import { useAppAccessContext } from 'contexts/AppAccessContext';

type HookFormProductionRunAutocompleteProps = {
  children?: ReactNode;
  defaultValue?: string | string[];
  disabled?: boolean;
  multiple?: boolean;
  sx?: SxProps<Theme>;
  helperText?: string;
  mt?: number;
  boxSx?: SxProps<Theme>;
  fullWidth?: boolean;
  // size is inherited from HookFormComponentProps, but explicitly included here
  // to prevent TypeScript from inferring it as 'unknown' due to the index signature
  size?: 'small' | 'medium';
  setValue: UseFormSetValue<FieldValues>;
  isInlineEdit?: boolean;
  contextualCreate?: boolean;
  // TODO: its super annoying to have to type this to handle both intermixed and I can foresee this being a pain point for other cases
  // I have an idea described in this ticket: https://linear.app/cofactr/issue/UI-3584/create-tolite-util-function
  // This spot would be a good first use case
  filterOptions?: (
    options: (ProductionRunLite | ProductionRun)[]
  ) => (ProductionRunLite | ProductionRun)[];
  noOptionsText?: string;
  teamId?: TeamLite['id'];
  [props: string]: unknown;
} & HookFormComponentProps;

export const HookFormProductionRunAutocomplete = ({
  errors,
  control,
  name = '',
  label,
  defaultValue,
  disabled,
  multiple = false,
  sx,
  mt = 2,
  boxSx,
  helperText,
  isInlineEdit,
  fullWidth = true,
  size = 'medium',
  setValue,
  filterOptions,
  noOptionsText,
  teamId,
  contextualCreate = true,
  shouldUnregister = false,
  ...rest
}: HookFormProductionRunAutocompleteProps) => {
  const [newProductionRunDialogOpen, setNewProductionRunDialogOpen] =
    useState(false);
  const [contextuallyCreatedOption, setContextuallyCreatedOption] =
    useState<ProductionRun | null>(null);
  const { data: productionRuns, isLoading: isLoadingProductionRuns } =
    useGetProductionRunsQuery({});
  const [getProductionRun, { data: productionRun }] =
    useLazyGetProductionRunQuery();

  const { hasAppPermission } = useAppAccessContext();
  const propertiesAppEnabled = hasAppPermission(ALL_APP_IDS.PROPERTIES);

  const options: (ProductionRunLite | ProductionRun)[] = useMemo(() => {
    const combinedOptions = [
      ...(productionRun ? [productionRun] : []),
      ...(productionRuns || []),
      ...(contextuallyCreatedOption ? [contextuallyCreatedOption] : []),
    ].filter((option) => option !== null && option !== undefined);

    const allOptions = _.chain(combinedOptions)
      .uniqBy('id')
      .filter((option) => {
        const optionTeamId = option?.team
          ? typeof option.team === 'string'
            ? option.team
            : option.team.id
          : undefined;
        return (
          option !== undefined &&
          (propertiesAppEnabled && teamId && optionTeamId
            ? optionTeamId === teamId
            : true)
        );
      })
      .value();

    return filterOptions ? filterOptions(allOptions) : allOptions;
  }, [
    productionRuns,
    contextuallyCreatedOption,
    productionRun,
    propertiesAppEnabled,
    teamId,
    filterOptions,
  ]);

  useEffect(() => {
    if (defaultValue) {
      const defaultProductionRun = _.find(
        productionRuns,
        (pr) => pr.id === defaultValue
      );
      if (!defaultProductionRun && typeof defaultValue === 'string') {
        getProductionRun(defaultValue);
      }
    }
  }, [defaultValue, productionRuns, getProductionRun]);

  const { field, fieldState } = useController({
    name,
    control,
    shouldUnregister,
  });
  const selectedOption = options.find((opt) => opt.id === field.value) || null;

  const AutoCompleteInput = (
    <Autocomplete
      loading={isLoadingProductionRuns}
      fullWidth={fullWidth}
      sx={{ mt, ...sx }}
      options={options}
      value={selectedOption}
      size={size}
      onChange={(__, data) => {
        field.onChange(data?.id ?? null);
      }}
      onBlur={field.onBlur}
      isOptionEqualToValue={(o, v) => o?.id === v?.id}
      getOptionLabel={(option) => option?.name || ''}
      noOptionsText={noOptionsText ?? 'No options'}
      renderInput={(params) => (
        <Box>
          <TextField {...params} label={label} fullWidth size={size} />
          {fieldState.error?.message && (
            <FormHelperText error id={`${name}Error`}>
              {fieldState.error.message}
            </FormHelperText>
          )}
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </Box>
      )}
      disabled={disabled}
    />
  );

  return (
    <Box sx={{ ...boxSx }}>
      {disabled || !contextualCreate ? (
        AutoCompleteInput
      ) : (
        <>
          <ContextualCreateButton
            onClick={() => setNewProductionRunDialogOpen(true)}
            permissionScope={{ app: ALL_APPS.PRODUCTION.id }}
            size={size}
            sx={
              isInlineEdit
                ? {
                    borderRadius: '0px',
                    borderRight: 'none',
                    height:
                      size === 'small'
                        ? '32px'
                        : size === 'medium'
                        ? '40px'
                        : '42px',
                    p: '5px',
                    minWidth: '0',
                    position: 'relative',
                    left: '-1px',
                    '.MuiButton-startIcon': { p: 0, m: 0 },
                  }
                : undefined
            }
          >
            {AutoCompleteInput}
          </ContextualCreateButton>
          <CreateEditProductionRunDialog
            dialogOpen={newProductionRunDialogOpen}
            onClose={() => setNewProductionRunDialogOpen(false)}
            onCreateSuccess={(value) => {
              setContextuallyCreatedOption(value);
              setValue && setValue(name, value.id);
            }}
          />
        </>
      )}
    </Box>
  );
};
